import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { colors, fonts } from '../theme/tokens';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { selectActiveModules, selectRoleId } from '../redux/session/session.selectors';
import { selectRole } from '../redux/auth/auth.selectors';
import { moduleForPath } from '../menu';

/* §2 App shell — grid 56px / 1fr, sidebar 236px, main content with fadeUp
   remount keyed by route:roleId. Shows "Not authorised" when the route's
   module is not in the active role's modules. */
export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const roleId = useSelector(selectRoleId);
  const modules = useSelector(selectActiveModules);
  const role = useSelector(selectRole);

  const moduleId = moduleForPath(location.pathname);
  const authorised = moduleId === undefined || modules.includes(moduleId);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: '56px 1fr',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: fonts.body,
      }}
    >
      <TopBar />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '236px 1fr',
          minHeight: 0,
        }}
      >
        <Sidebar />
        <main style={{ overflowY: 'auto', background: colors.cream }}>
          <div
            key={`${location.pathname}:${roleId ?? ''}`}
            style={{ animation: 'fadeUp .32s ease both', padding: '0 26px 48px' }}
          >
            {authorised ? (
              children
            ) : (
              <NotAuthorised roleName={role?.name ?? 'this role'} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const NotAuthorised: React.FC<{ roleName: string }> = ({ roleName }) => (
  <div
    style={{
      maxWidth: 520,
      margin: '80px auto 0',
      background: '#fff',
      borderRadius: 14,
      padding: 40,
      textAlign: 'center',
      boxShadow: '0 4px 18px rgba(28,42,38,.07)',
    }}
  >
    <div style={{ fontFamily: fonts.serif, fontSize: 22, color: colors.danger }}>Not authorised</div>
    <div style={{ fontSize: 13.5, color: colors.muted, marginTop: 8 }}>
      The role “{roleName}” does not have access to this module. Switch role from the top bar to
      view it.
    </div>
  </div>
);

export default AppShell;
